import Accessories from "@components/steps/Accessories";
import ContactInfo from "@components/steps/ContactInfo";
import OtherCredentialsVerification from "@components/steps/OtherCredentialsVerification";
import ProfessionalInfo from "@components/steps/ProfessionalInfo";
import ShippingAddress from "@components/steps/ShippingAddress";
import type { AccessoryProduct } from "@constants/formDefaults";
import { getMockupData } from "@constants/mockup/mockupFormDefaults";
import { PRODUCTS } from "@constants/prices";
import { useCartInfo } from "@contexts/CartInfoContext";
import { usePurchaseFlow as usePurchaseFlowContext } from "@contexts/PurchaseFlowContext";
import { useToken } from "@contexts/TokenContext";
import { useShippingCalculation } from "@hooks/stepForm/useShippingCalculation";
import useGTM from "@hooks/useGTM";
import { Button, Card, Stack } from "@mui/material";
import { getPrice, type SupportedCurrency } from "@utils/currencyUtils";
import { createGTMItems } from "@utils/stepFormUtils";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import LoadingOverlay from "./LoadingOverlay";

// Define the step component type
export type StepComponent = React.ComponentType<{ hasAttemptedValidation?: boolean }> | null;

export default function StepForm() {
  // Get state from contexts
  const { isAuthenticated } = useToken();
  const {
    stepComponents,
    currentStep,
    stepTitles,
    authenticatedStepTitles,
    hasAccessoryStep,
    hasShippingStep,
    hasAttemptedValidation,
    setHasAttemptedValidation,
    isLoading,
    loadingMessage,
    setLoadingMessage,
    goBack,
    isMockup,
    handleCreatePurchase,
    handleConfirmPurchase,
    updatePurchase,
    isOnConfirmStep,
  } = usePurchaseFlowContext();
  const { setServerCartInfo, resetForConfirmPage } = useCartInfo();
  const location = useLocation();

  // Track previous confirm step state to detect when leaving
  const prevIsOnConfirmStepRef = useRef(isOnConfirmStep);

  const methods = useFormContext();

  // Watch all form fields once to reduce subscriptions
  const formValues = methods.watch();

  const uuid = formValues.uuid;
  const products = formValues.items;
  const accessoryProducts = formValues.accessoryProducts;
  const selectedCurrency = (formValues.selectedCurrency as SupportedCurrency) || "usd";

  const CurrentStepComponent = stepComponents[currentStep - 1] as StepComponent;

  // Use shipping calculation hook
  const { shippingError, areShippingFieldsComplete, handleCalculateShipping, isFetchingShipping } =
    useShippingCalculation(CurrentStepComponent, formValues, setHasAttemptedValidation);

  const {
    addProfessionalCredentials,
    addToCart,
    updateEcommerce,
    beginCheckout,
    addShippingInfo,
    addPaymentInfo,
  } = useGTM();

  // Custom goNext with step-specific logic
  const goNext = async () => {
    setHasAttemptedValidation(true);
    const valid = await methods.trigger();
    const values = methods.getValues();

    if (!valid) return;

    // Handle step-specific logic
    if (CurrentStepComponent === ContactInfo) {
      if (!uuid) {
        handleCreatePurchase(values, uuid);
      }
    } else if (CurrentStepComponent === ProfessionalInfo) {
      addProfessionalCredentials({ professional_credentials: values.professionalCredentials });
    } else if (CurrentStepComponent === Accessories) {
      const gtmItems = createGTMItems(products, accessoryProducts);
      addToCart({ items: gtmItems });
    } else if (CurrentStepComponent === ShippingAddress) {
      const shipmentRate = methods.getValues("shipmentRate");
      addShippingInfo({ shipping_tier: shipmentRate?.serviceCode || "" });
    } else {
      try {
        setLoadingMessage("calculating tax...");
        const updatePayload = {
          uuid,
          items: products,
          shipping_address: {
            address1: values.address1,
            address2: values.address2 || "",
            city: values.city,
            state: values.state,
            country: values.country,
            postal_code: values.postalCode,
          },
          shipment_rate: values.shipmentRate,
        };

        const { data } = await updatePurchase(updatePayload);

        if (data.cart_info) {
          setServerCartInfo(data.cart_info);
        }

        if (data.totalTax !== undefined) {
          methods.setValue("totalTax", data.totalTax);
        }

        if (data.updateError) {
          // Continue anyway, tax will be calculated later if needed
        }
      } catch (err) {
        // Continue anyway, tax will be calculated later if needed
        console.error(err);
      }
    }

    // Advance to next step (handled by context's goNext if we called it,
    // but this is a custom goNext with step-specific logic)
    setHasAttemptedValidation(false);
    methods.setValue("currentStep", currentStep + 1);
  };

  // Pre-fill form data for mockup mode
  // Note: Authenticated user data is pre-filled in StoreLayout to avoid duplicate setValue calls
  // Preserve URL-derived fields when resetting to mockup data
  useEffect(() => {
    if (isMockup) {
      const mockupData = getMockupData();
      // Preserve URL-derived fields
      const currentValues = methods.getValues();
      const preservedFields = {
        items: currentValues.items,
        cartInfo: currentValues.cartInfo,
        selectedCurrency: currentValues.selectedCurrency,
        productCategories: currentValues.productCategories,
        accessoryCategories: currentValues.accessoryCategories,
        redirectUrl: currentValues.redirectUrl,
      };
      methods.reset({ ...mockupData, ...preservedFields });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMockup]);

  // Targeted validation - only validate errored fields when user types in them
  useEffect(() => {
    if (!hasAttemptedValidation) return;

    const subscription = methods.watch((_, { name, type }) => {
      // Only trigger validation if we have a field name and it's a change event
      if (name && type === "change") {
        // Check if this specific field has an error directly
        const fieldError = methods.formState.errors[name];

        if (fieldError) {
          // Validate the specific field that has an error
          methods.trigger(name);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [hasAttemptedValidation]);

  const items = formValues.items;
  const gtmFired = useRef(false);
  useEffect(() => {
    if (items.length > 0 && !gtmFired.current) {
      const totalValue = items.reduce((sum: number, item: { sku: string; quantity: number }) => {
        const product = PRODUCTS[item.sku];
        if (!product) return sum;
        const price = getPrice(product, "price", selectedCurrency);
        return sum + price * item.quantity;
      }, 0);
      updateEcommerce({
        currency: selectedCurrency.toUpperCase(),
        value: totalValue,
        items: items.map((item: { sku: string; quantity: number }) => {
          const product = PRODUCTS[item.sku];
          const productMeta = methods
            .getValues("accessoryProducts")
            ?.find((p: AccessoryProduct) => p.SKU === item.sku);

          const price = product
            ? getPrice(product, "price", selectedCurrency)
            : (productMeta?.PriceTier1 ?? 0);

          return {
            item_id: item.sku,
            item_name: productMeta?.Name ?? (product?.cartTitle || item.sku),
            quantity: item.quantity,
            price: price,
            item_category: productMeta?.productCategories?.join(", ") ?? "",
          };
        }),
      });

      beginCheckout();
      gtmFired.current = true;
    }
  }, [items, selectedCurrency, updateEcommerce, beginCheckout, methods]);

  useEffect(() => {
    if (formValues.stripePaymentMethodId) {
      addPaymentInfo({ payment_type: "Credit Card" });
    }
  }, [formValues.stripePaymentMethodId, addPaymentInfo]);

  // Reset shipping options whenever user lands on ShippingAddress step
  useEffect(() => {
    if (CurrentStepComponent === ShippingAddress) {
      methods.setValue("shippingOptions", []);
      methods.setValue("shipmentRate", null);
      methods.clearErrors("shipmentRate");
      setLoadingMessage("retreiving shipping rates...");
    }
  }, [CurrentStepComponent]);

  // Clear shipping options when address fields are edited after shipping has been calculated
  useEffect(() => {
    // Only activate when on shipping address step and shipping options exist
    if (CurrentStepComponent !== ShippingAddress || formValues.shippingOptions.length === 0) {
      return;
    }

    const subscription = methods.watch((_, { name, type }) => {
      // Only respond to change events (not initial load) for address fields
      if (
        type === "change" &&
        name &&
        ["address1", "city", "state", "country", "postalCode"].includes(name)
      ) {
        // Clear shipping options and selected option
        methods.setValue("shippingOptions", []);
        methods.setValue("shipmentRate", null);

        // Clear any shipping-related validation errors
        methods.clearErrors("shipmentRate");
      }
    });

    return () => subscription.unsubscribe();
  }, [CurrentStepComponent, formValues.shippingOptions.length]);

  // Reset cart display to optimistic view when leaving the Confirm step (back navigation only)
  useEffect(() => {
    const isPurchaseCompletePage = location.pathname.includes("/purchase-complete");

    // Only reset when going BACK from confirm step (not forward to purchase-complete)
    if (prevIsOnConfirmStepRef.current && !isOnConfirmStep && !isPurchaseCompletePage) {
      resetForConfirmPage();
    }
    prevIsOnConfirmStepRef.current = isOnConfirmStep;
  }, [isOnConfirmStep, resetForConfirmPage, location.pathname]);

  // Step component props configuration - using array index instead of component names
  // to avoid minification issues in production builds
  // This array must match the order of stepComponents from useStepFlow
  const stepPropsArray = useMemo(() => {
    const props = [];

    // Skip ContactInfo and ProfessionalInfo for authenticated users
    if (!isAuthenticated) {
      // ContactInfo
      props.push({
        title: stepTitles.ContactInfo,
        hasAttemptedValidation,
      });

      // ProfessionalInfo
      props.push({
        hasAttemptedValidation,
        title: stepTitles.ProfessionalInfo,
      });

      // OtherCredentialsVerification (conditional)
      if (stepTitles.OtherCredentialsVerification) {
        props.push({
          title: stepTitles.OtherCredentialsVerification,
        });
      }
    }

    // Accessories (conditional)
    if (hasAccessoryStep) {
      props.push({
        hasAttemptedValidation,
        goNext,
        goBack,
        isLoading,
        title: stepTitles.Accessories,
      });
    }

    // ShippingAddress (conditional)
    if (hasShippingStep) {
      props.push({
        hasAttemptedValidation,
        shippingError,
        title: stepTitles.ShippingAddress,
        isAuthenticated,
      });
    }

    // ConfirmPurchase (always last)
    props.push({
      hasAttemptedValidation,
      title: stepTitles.ConfirmPurchase,
    });

    return props;
  }, [
    stepTitles,
    hasAttemptedValidation,
    isLoading,
    shippingError,
    isAuthenticated,
    hasAccessoryStep,
    hasShippingStep,
    goNext,
    goBack,
  ]);

  // Send step title to parent iframe window
  useEffect(() => {
    if (window.parent !== window) {
      // Only send if we're in an iframe
      const currentStepProps = stepPropsArray[currentStep - 1];
      const stepTitle = currentStepProps?.title;

      // For authenticated users, try to get the authenticated title
      let titleToSend = stepTitle;
      if (isAuthenticated && stepTitle) {
        // Find which step component we're on and get its authenticated title
        const currentComponent = stepComponents[currentStep - 1];
        const componentName = currentComponent?.name;
        // Look up authenticated title by component name
        if (componentName && authenticatedStepTitles[componentName]) {
          titleToSend = authenticatedStepTitles[componentName];
        }
      }

      if (titleToSend) {
        window.parent.postMessage(
          {
            action: "setTitle",
            title: titleToSend,
          },
          "*", // TODO: Do we need to restrict this to a specific origin for security?
        );
      }
    }
  }, [currentStep, stepPropsArray, isAuthenticated, stepComponents, authenticatedStepTitles]);

  return (
    <Card variant="outlined" sx={{ p: 1 }}>
      {CurrentStepComponent && <CurrentStepComponent {...stepPropsArray[currentStep - 1]} />}

      {CurrentStepComponent !== Accessories && (
        <Stack
          m={2}
          justifyContent={CurrentStepComponent === ContactInfo ? "flex-end" : "space-between"}
          direction="row"
        >
          {CurrentStepComponent !== ContactInfo && (
            <Button onClick={goBack} loading={isLoading}>
              Back
            </Button>
          )}
          {CurrentStepComponent === ShippingAddress && (
            <Button
              variant={formValues.shippingOptions?.length > 0 ? "outlined" : "contained"}
              loading={isFetchingShipping}
              color="primary"
              onClick={handleCalculateShipping}
              disabled={!areShippingFieldsComplete}
            >
              Calculate Shipping
            </Button>
          )}
          {CurrentStepComponent !== OtherCredentialsVerification && (
            <Button
              variant="contained"
              onClick={currentStep === stepComponents.length ? handleConfirmPurchase : goNext}
              loading={isLoading}
              disabled={!methods.formState.isValid}
            >
              {currentStep === stepComponents.length ? "Confirm Purchase" : "Continue"}
            </Button>
          )}
        </Stack>
      )}

      <LoadingOverlay open={isLoading || isFetchingShipping} message={loadingMessage} />
    </Card>
  );
}
